import type { SupabaseClient } from '@supabase/supabase-js'
import { supabaseDailyApi } from '../daily/dailyApi'
import type { DailyApi } from '../daily/dailyApi'
import { supabaseProfileApi } from '../profile/profileApi'
import type { ProfileApi } from '../profile/profileApi'
import type { Account, BrowserEnv, GuestReason } from './account'
import type { AuthUser } from './authApi'
import { guestName } from './guestName'
import { NAME_KEY, readLocal, removeLocal, writeLocal } from './storage'

export interface AuthSession {
  /** `offline`: no Supabase configured or reachable; the server must be in dev mode to accept. */
  mode: 'online' | 'offline'
  /** The current user (changes on sign-out without a reload); null offline. */
  readonly userId: string | null
  displayName: string
  /** Fresh access token for each room join; `""` when offline. */
  getToken: () => Promise<string>
  /** Own profile and recent races (T2.4); null offline. */
  profile: ProfileApi | null
  /** Own daily task rows (T2.5); null offline. */
  daily: DailyApi | null
  /** Sign-in, sign-out and the OAuth return (T2.6); null offline. */
  account: Account | null
}

export interface AuthEnv {
  url?: string | undefined
  anonKey?: string | undefined
}

/** The 0001 trigger's name for a new user, replaced by the local guest name at sign-in. */
const PLACEHOLDER_RE = /^guest-[0-9a-f]{6}$/

/** The local guest name, generated once per browser. */
export function localGuestName(): string {
  const cached = readLocal(NAME_KEY)
  if (cached) return cached
  const name = guestName()
  writeLocal(NAME_KEY, name)
  return name
}

function offline(displayName: string): AuthSession {
  return {
    mode: 'offline',
    userId: null,
    displayName,
    getToken: () => Promise.resolve(''),
    profile: null,
    daily: null,
    account: null,
  }
}

type NameRow = { kind: 'row'; name: string } | { kind: 'missing' } | { kind: 'unknown' }

async function ownName(supabase: SupabaseClient, userId: string): Promise<NameRow> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('id', userId)
      .maybeSingle()
    if (error) return { kind: 'unknown' }
    const row = data as { display_name?: unknown } | null
    if (!row) return { kind: 'missing' }
    return { kind: 'row', name: typeof row.display_name === 'string' ? row.display_name : '' }
  } catch {
    return { kind: 'unknown' }
  }
}

/**
 * Names follow the profile (T2.6): a new profile (the trigger's `guest-xxxxxx`) gets the local
 * guest name; an existing one (signed in on another device, or renamed) becomes the local name,
 * so other tabs and the server agree.
 */
async function syncName(supabase: SupabaseClient, row: NameRow): Promise<void> {
  if (row.kind !== 'row') return
  if (!PLACEHOLDER_RE.test(row.name)) {
    if (row.name && row.name !== readLocal(NAME_KEY)) writeLocal(NAME_KEY, row.name)
    return
  }
  const { error } = await supabase.rpc('set_display_name', { new_name: localGuestName() })
  if (error) console.warn('[auth] set_display_name failed', error.message)
}

function inIframe(): boolean {
  try {
    return window.top !== window.self
  } catch {
    return true
  }
}

function browserEnv(): BrowserEnv {
  return {
    href: () => window.location.href,
    replaceUrl: (url) => window.history.replaceState(window.history.state, '', url),
    origin: window.location.origin,
    inIframe: inIframe(),
    read: readLocal,
    write: (key, value) => (value === null ? removeLocal(key) : writeLocal(key, value)),
    now: () => Date.now(),
  }
}

/**
 * Sign in (GAME_PLAN §5.6, T2.6): reuse the cached session or sign in anonymously, finish an
 * OAuth return (supabase-js does the PKCE exchange), repair a stale session once, and keep the
 * name in step with the profile. Empty VITE_SUPABASE_* → offline guest mode: no Supabase calls,
 * token `""`, local guest name, no sign-in UI. supabase-js is loaded only when needed.
 */
export async function initAuth(env: AuthEnv): Promise<AuthSession> {
  const name = localGuestName()
  if (!env.url || !env.anonKey) {
    console.info('[auth] offline guest mode (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY empty)', {
      name,
    })
    return offline(name)
  }
  try {
    // supabase-js and the account code load only when online, so the menu's first JS stays small.
    const [{ createClient }, { Account }, { supabaseAuthApi }] = await Promise.all([
      import('@supabase/supabase-js'),
      import('./account'),
      import('./authApi'),
    ])
    const supabase = createClient(env.url, env.anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        storageKey: 'runkr.auth',
        // OAuth returns `?code=` and supabase-js exchanges it at load (T2.6).
        flowType: 'pkce',
        detectSessionInUrl: true,
      },
    })
    const sync = async (user: AuthUser, why: GuestReason): Promise<void> => {
      // A new guest after sign-out gets a new name, not the signed-out account's; a repaired
      // stale session keeps the player's name.
      if (why === 'signOut') writeLocal(NAME_KEY, guestName())
      await syncName(supabase, await ownName(supabase, user.id))
    }
    const api = supabaseAuthApi(supabase, { url: env.url, anonKey: env.anonKey })
    const account = new Account(api, browserEnv(), sync)
    let user = await account.start()
    const row = await ownName(supabase, user.id)
    if (row.kind === 'missing') user = (await account.renewStaleSession()) ?? user
    else await syncName(supabase, row)
    console.info('[auth] signed in', user.id, { anonymous: user.isAnonymous })
    const current = (): string => account.user.get()?.id ?? user.id
    return {
      mode: 'online',
      get userId() {
        return current()
      },
      displayName: readLocal(NAME_KEY) ?? name,
      getToken: async () => (await supabase.auth.getSession()).data.session?.access_token ?? '',
      profile: supabaseProfileApi(supabase),
      daily: supabaseDailyApi(supabase, current),
      account,
    }
  } catch (err) {
    console.warn('[auth] sign-in failed; continuing as offline guest', err)
    return offline(name)
  }
}
